import React, { useCallback, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import { MdExpandMore } from 'react-icons/md';

import Layout from '../Layout';
import {
  analyserDommage, appliquerModele, chercherBareme, chiffrerDommage,
  listerDommages, modelesPourDommage, modifierPoste,
  propositionsPourDommage, retirerChiffrage,
} from '../api';

const euros = (v) =>
  v === null || v === undefined
    ? '—'
    : `${Number(v).toLocaleString('fr-BE', { minimumFractionDigits: 2,
        maximumFractionDigits: 2 })} €`;

const nombre = (v) =>
  Number(v).toLocaleString('fr-BE', { maximumFractionDigits: 2 });

/**
 * Fourchette de prix d'une ligne de barème.
 *
 * Une ligne en pourcentage porte un taux, pas un prix : « 1 à 8 % ». L'écrire
 * « 1,00 à 8,00 €/% » ne veut rien dire.
 */
const fourchette = (min, max, unite) =>
  unite === '%'
    ? `${nombre(min)} à ${nombre(max)} %`
    : `${euros(min)} à ${euros(max)} / ${unite}`;

/** Le verdict de l'assistant : ce qu'il comprend, ce qu'il suppose, ce qu'il chiffre. */
function Analyse({ analyse }) {
  if (!analyse) return null;
  const indetermine = analyse.intervention === 'indetermine';

  return (
    <Alert severity={indetermine ? 'warning' : 'info'} sx={{ mb: 3 }}>
      <AlertTitle>
        {indetermine
          ? 'Deux options possibles — il manque un élément pour trancher'
          : analyse.intervention === 'reparation'
            ? 'Une réparation suffit'
            : 'Un remplacement paraît nécessaire'}
      </AlertTitle>

      {analyse.nature && (
        <Typography variant="body2" sx={{ mb: 1 }}>{analyse.nature}</Typography>
      )}
      {analyse.justification && (
        <Typography variant="body2" sx={{ mb: 1 }}>{analyse.justification}</Typography>
      )}
      {analyse.hypothese && (
        <Typography variant="body2" sx={{ mb: 1, fontStyle: 'italic' }}>
          Ce qui reste supposé : {analyse.hypothese}
        </Typography>
      )}

      {Object.keys(analyse.options || {}).length > 0 && (
        <Stack spacing={0.5} sx={{ my: 1.5 }}>
          {Object.entries(analyse.options).map(([nom, o]) => (
            <Typography key={nom} variant="body2">
              <strong>{nom === 'reparation' ? 'Réparation' : 'Remplacement'}</strong>
              {' : '}{euros(o.total_min)} à {euros(o.total_max)}
            </Typography>
          ))}
        </Stack>
      )}

      {analyse.vigilance?.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600, mt: 1 }}>
            Ce qu'une photo ne montre pas
          </Typography>
          <ul style={{ margin: '4px 0 8px', paddingLeft: 20 }}>
            {analyse.vigilance.map((v, i) => (
              <li key={i}><Typography variant="body2">{v}</Typography></li>
            ))}
          </ul>
        </>
      )}

      {analyse.questions?.length > 0 && (
        <>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            À vérifier avant de chiffrer
          </Typography>
          <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
            {analyse.questions.map((q, i) => (
              <li key={i}><Typography variant="body2">{q}</Typography></li>
            ))}
          </ul>
        </>
      )}

      {analyse.photo_ecartee && (
        <Typography variant="body2" color="warning.main" sx={{ mt: 1.5 }}>
          {analyse.photo_ecartee}
        </Typography>
      )}
    </Alert>
  );
}

/**
 * Une réparation type, avec le détail de ce qu'elle comprend.
 *
 * Le détail est montré mais jamais à cocher : c'est ce qui rend le montant
 * défendable sans demander à l'assuré de faire le métier d'un expert.
 */
function Modele({ modele, variantes, onVariante, occupe, onAppliquer }) {
  const groupes = Object.entries(modele.choix || {});
  // Quantités corrigées par l'assuré. Vide = celle déduite du dommage ;
  // zéro = la ligne ne sera pas chiffrée.
  const [quantites, setQuantites] = useState({});

  const quantiteDe = (l) =>
    quantites[l.code] !== undefined ? quantites[l.code] : Number(l.quantite);

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
             justifyContent="space-between" alignItems={{ sm: 'flex-start' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {modele.titre}
          </Typography>
          {modele.description && (
            <Typography variant="body2" color="text.secondary">
              {modele.description}
            </Typography>
          )}
          <Typography variant="body1" sx={{ mt: 1 }}>
            {euros(modele.total_min)} à {euros(modele.total_max)}
          </Typography>
          {Object.keys(quantites).length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Quantités modifiées — le montant sera recalculé à la validation.
            </Typography>
          )}
        </Box>
        <Button variant="contained" disabled={occupe}
                onClick={() => onAppliquer(quantites)}>
          Retenir cette réparation
        </Button>
      </Stack>

      {groupes.map(([groupe, options]) => {
        const retenue = variantes[groupe]
          ?? options.find((o) => o.par_defaut)?.ligne_id
          ?? options[0].ligne_id;
        return (
          <Box key={groupe} sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {groupe}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={retenue}
              onChange={(e, valeur) => valeur && onVariante(groupe, valeur)}
              disabled={occupe}
              sx={{ flexWrap: 'wrap' }}
            >
              {options.map((o) => (
                <ToggleButton key={o.ligne_id} value={o.ligne_id}
                              sx={{ textTransform: 'none' }}>
                  <Stack alignItems="flex-start">
                    <span>{o.libelle}</span>
                    <Typography variant="caption" color="text.secondary">
                      {fourchette(o.unityMin, o.unityMax, o.unite)}
                    </Typography>
                  </Stack>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        );
      })}

      <Accordion variant="outlined" disableGutters elevation={0}
                 sx={{ mt: 1.5, border: 0, '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<MdExpandMore />} sx={{ px: 0 }}>
          <Typography variant="body2" color="text.secondary">
            Ce que cela comprend ({modele.lignes.length} postes) — quantités
            modifiables
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 0 }}>
          <Stack spacing={0.5}>
            {modele.lignes.map((l) => {
              const calculee = l.base === 'POURCENTAGE';
              const quantite = quantiteDe(l);
              const part = Number(l.quantite) > 0
                ? Number(quantite) / Number(l.quantite) : 1;
              return (
                <Stack key={l.ligne_id ?? l.code} direction="row" spacing={2}
                       alignItems="center" justifyContent="space-between">
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">{l.libelle}</Typography>
                    {l.contexte && (
                      <Typography variant="caption" color="text.secondary">
                        {l.contexte}
                      </Typography>
                    )}
                  </Box>
                  {calculee ? (
                    <Typography variant="body2" color="text.secondary"
                                sx={{ width: 150, textAlign: 'right' }}>
                      calculé sur les travaux
                    </Typography>
                  ) : (
                    <TextField
                      size="small"
                      type="number"
                      value={quantite}
                      onChange={(e) => setQuantites((q) => ({
                        ...q, [l.code]: e.target.value,
                      }))}
                      disabled={occupe}
                      inputProps={{ min: 0, step: 'any',
                                    'aria-label': `Quantité — ${l.libelle}` }}
                      InputProps={{ endAdornment: (
                        <Typography variant="caption" color="text.secondary">
                          {l.unite}
                        </Typography>
                      ) }}
                      sx={{ width: 150 }}
                    />
                  )}
                  <Typography variant="body2" color="text.secondary"
                              sx={{ minWidth: 150, textAlign: 'right' }}>
                    {Number(quantite) > 0
                      ? `${euros(l.total_min * part)} à ${euros(l.total_max * part)}`
                      : 'écartée'}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}

/** Le chiffrage d'un dommage : réparations types, puis détail si besoin. */
function BlocDommage({ dommage, onChiffre }) {
  const [propositions, setPropositions] = useState(null);
  const [modeles, setModeles] = useState(null);
  const [surface, setSurface] = useState(null);
  const [pays, setPays] = useState(null);
  // Lignes de barème cherchées à la main : celles que les propositions ne
  // trouvent jamais, comme l'installation de chantier.
  const [ajoutees, setAjoutees] = useState([]);
  const [recherche, setRecherche] = useState('');
  const [trouvees, setTrouvees] = useState([]);
  const [choix, setChoix] = useState({});
  // Variante retenue par groupe. Changer d'avis relance le calcul côté
  // serveur : un montant ne se recalcule jamais dans le navigateur.
  const [variantes, setVariantes] = useState({});
  const [analyse, setAnalyse] = useState(null);
  const [occupe, setOccupe] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState(null);

  useEffect(() => {
    let vivant = true;
    propositionsPourDommage(dommage.id)
      .then((d) => {
        if (!vivant) return;
        setPropositions(d.propositions);
        setSurface(d.surface_calculee);
        setPays(d.country);
      })
      .catch(() => vivant && setErreur('Impossible de charger les propositions.'));
    return () => { vivant = false; };
  }, [dommage.id]);

  useEffect(() => {
    let vivant = true;
    modelesPourDommage(dommage.id, variantes)
      .then((d) => vivant && setModeles(d.modeles))
      .catch(() => vivant && setModeles([]));
    return () => { vivant = false; };
  }, [dommage.id, variantes]);

  const poserModele = async (modeleId, quantites) => {
    setOccupe(true);
    setErreur('');
    try {
      // Une quantité laissée vide vaut celle déduite du dommage ; une
      // quantité à zéro écarte la ligne, c'est ainsi qu'on retire un poste.
      const corrigees = Object.fromEntries(
        Object.entries(quantites || {})
          .filter(([, q]) => q !== '' && !Number.isNaN(Number(q)))
          .map(([code, q]) => [code, Number(q)]));
      setResultat(
        await appliquerModele(dommage.id, modeleId, corrigees, variantes));
      onChiffre();
    } catch (e) {
      setErreur(e?.response?.data?.detail
        || "Le chiffrage n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  useEffect(() => {
    let vivant = true;
    const minuteur = setTimeout(() => {
      chercherBareme(recherche, pays)
        .then((r) => vivant && setTrouvees(r))
        .catch(() => vivant && setTrouvees([]));
    }, 300);
    return () => { vivant = false; clearTimeout(minuteur); };
  }, [recherche, pays]);

  const ajouter = (ligne) => {
    setAjoutees((a) => (a.some((l) => l.code === ligne.code) ? a : [...a, ligne]));
    setChoix((c) => ({ ...c, [ligne.code]: c[ligne.code] ?? (surface ?? '') }));
    setRecherche('');
    setTrouvees([]);
  };

  // Vide tant que l'assuré n'a rien arrêté : on pré-remplit avec le calcul
  // du barème, qu'il garde ou remplace.
  const [reclame, setReclame] = useState('');

  useEffect(() => {
    const chiffrage = dommage.chiffrage;
    if (!chiffrage) { setReclame(''); return; }
    setReclame(Number(chiffrage.reclamer) > 0
      ? String(chiffrage.reclamer) : String(chiffrage.calculer ?? ''));
  }, [dommage.chiffrage]);

  const enregistrerReclame = async () => {
    setOccupe(true);
    setErreur('');
    try {
      await modifierPoste(dommage.chiffrage.poste_id,
                          { reclamer: reclame === '' ? 0 : reclame });
      onChiffre();
    } catch {
      setErreur("Le montant n'a pas pu être enregistré.");
    } finally {
      setOccupe(false);
    }
  };

  const retirer = async () => {
    setOccupe(true);
    setErreur('');
    try {
      await retirerChiffrage(dommage.id);
      setResultat(null);
      onChiffre();
    } catch {
      setErreur("Le retrait n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  const basculer = (code) =>
    setChoix((c) => {
      const suivant = { ...c };
      if (suivant[code] !== undefined) delete suivant[code];
      else suivant[code] = surface ?? '';
      return suivant;
    });

  const demanderAnalyse = async () => {
    setAnalyseEnCours(true);
    setErreur('');
    try {
      setAnalyse(await analyserDommage(dommage.id));
    } catch {
      setErreur("L'analyse n'a pas abouti. Réessayez dans un instant.");
    } finally {
      setAnalyseEnCours(false);
    }
  };

  const chiffrer = async () => {
    const lignes = Object.entries(choix)
      .filter(([, q]) => q !== '' && Number(q) > 0)
      .map(([code, q]) => ({ code, quantity: Number(q) }));
    if (!lignes.length) {
      setErreur('Choisissez au moins une ligne et indiquez sa quantité.');
      return;
    }
    setOccupe(true);
    setErreur('');
    try {
      const r = await chiffrerDommage(dommage.id, lignes);
      setResultat(r);
      setChoix({});
      onChiffre();
    } catch {
      setErreur("Le chiffrage n'a pas abouti.");
    } finally {
      setOccupe(false);
    }
  };

  return (
    <Accordion defaultExpanded variant="outlined" disableGutters
               sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary expandIcon={<MdExpandMore />}>
        <Stack direction="row" spacing={2} alignItems="baseline"
               sx={{ width: '100%' }}>
          <Typography variant="h3">
            {dommage.element_libelle || dommage.element} — {dommage.piece || 'pièce non précisée'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {dommage.composition}
            {surface ? ` · ${surface} m²` : ''}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {dommage.chiffrage && (
            <Chip color="success" variant="outlined" size="small"
                  label={`Chiffré ${euros(dommage.chiffrage.total)}`} />
          )}
        </Stack>
      </AccordionSummary>

      <AccordionDetails>
        {erreur && <Alert severity="error" sx={{ mb: 2 }}>{erreur}</Alert>}

        <Analyse analyse={analyse} />

        <Button variant="outlined" onClick={demanderAnalyse}
                disabled={analyseEnCours} sx={{ mb: 3 }}>
          {analyseEnCours
            ? 'Analyse en cours…'
            : analyse ? 'Refaire une analyse' : 'Que faut-il faire ?'}
        </Button>

        {modeles === null && <CircularProgress size={24} />}

        {modeles?.length > 0 && (
          <Stack spacing={2} sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Choisissez la réparation qui correspond à votre situation. Chacune
              comprend l'ensemble des travaux nécessaires — vous n'avez rien à
              détailler.
            </Typography>
            {modeles.map((m) => (
              <Modele key={m.id} modele={m} occupe={occupe}
                      variantes={variantes}
                      onVariante={(groupe, ligneId) =>
                        setVariantes((v) => ({ ...v, [groupe]: ligneId }))}
                      onAppliquer={(q) => poserModele(m.id, q)} />
            ))}
          </Stack>
        )}

        {modeles?.length === 0 && (
          <Alert severity="info" sx={{ mb: 3 }}>
            Aucune réparation type ne correspond encore à ce dommage. Vous
            pouvez composer le chiffrage ligne par ligne ci-dessous, ou préciser
            en quoi l'élément est fait.
          </Alert>
        )}

        <Accordion variant="outlined" disableGutters
                   sx={{ '&:before': { display: 'none' }, mb: 2 }}>
          <AccordionSummary expandIcon={<MdExpandMore />}>
            <Typography variant="body1" sx={{ fontWeight: 600 }}>
              Ajouter d'autres travaux
            </Typography>
          </AccordionSummary>
          <AccordionDetails>

        <Autocomplete
          freeSolo
          options={trouvees}
          filterOptions={(o) => o}
          inputValue={recherche}
          onInputChange={(e, valeur) => setRecherche(valeur)}
          onChange={(e, valeur) => valeur?.code && ajouter(valeur)}
          getOptionLabel={(o) => (typeof o === 'string' ? o : o.title || '')}
          renderOption={(props, o) => (
            <li {...props} key={o.code}>
              <Box>
                <Typography variant="body2">
                  {o.specification || o.title}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {[o.chapter_label, o.section_label].filter(Boolean).join(' › ')}
                  {' · '}{fourchette(o.unityMin, o.unityMax,
                                     o.unit_normalized || o.unit)}
                </Typography>
              </Box>
            </li>
          )}
          noOptionsText={recherche.length < 2
            ? 'Tapez au moins deux lettres'
            : 'Aucun poste du barème ne correspond'}
          renderInput={(params) => (
            <TextField {...params} sx={{ mb: 2 }}
                       label="Chercher un poste dans le barème"
                       placeholder="installation de chantier, plinthe, enduit…" />
          )}
        />

        {propositions === null && <CircularProgress size={24} />}

        {propositions?.length === 0 && (
          <Alert severity="info">
            Aucun poste du barème ne correspond.
          </Alert>
        )}

        {(propositions?.length > 0 || ajoutees.length > 0) && (
          <Stack spacing={1}>

            {[...ajoutees,
              ...(propositions || []).filter(
                (p) => !ajoutees.some((a) => a.code === p.code))].map((p) => {
              const retenu = choix[p.code] !== undefined;
              return (
                <Paper key={p.code} variant="outlined"
                       sx={{ p: 1.5, bgcolor: retenu ? 'action.hover' : undefined }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}
                         alignItems={{ sm: 'center' }}>
                    <Checkbox checked={retenu} onChange={() => basculer(p.code)}
                              sx={{ p: 0.5 }} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {p.specification || p.section_label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.section_label || p.chapter_label}
                        {' · '}{fourchette(p.unityMin, p.unityMax,
                                        p.unit_normalized || p.unit)}
                      </Typography>
                    </Box>
                    {p.origine === 'OBSERVE' && (
                      <Chip size="small" color="secondary" variant="outlined"
                            label={`marché · ${p.observations} relevés`} />
                    )}
                    {retenu && ((p.unit_normalized || p.unit) === '%' ? (
                      <Typography variant="caption" color="text.secondary"
                                  sx={{ width: 130, textAlign: 'right' }}>
                        calculé sur les travaux
                      </Typography>
                    ) : (
                      <TextField
                        size="small" type="number" label="Quantité"
                        value={choix[p.code]}
                        onChange={(e) =>
                          setChoix((c) => ({ ...c, [p.code]: e.target.value }))}
                        sx={{ width: 130 }}
                        InputProps={{ endAdornment: p.unit_normalized || p.unit }}
                      />
                    ))}
                  </Stack>
                </Paper>
              );
            })}

            <Stack direction="row" justifyContent="flex-end" sx={{ pt: 1 }}>
              <Button variant="contained" onClick={chiffrer} disabled={occupe}>
                Ajouter au chiffrage
              </Button>
            </Stack>
          </Stack>
        )}
          </AccordionDetails>
        </Accordion>

        {dommage.chiffrage && (
          <>
            <Divider sx={{ my: 2 }} />
            <Alert
              severity="success"
              action={
                <Button color="inherit" size="small" disabled={occupe}
                        onClick={retirer}>
                  Retirer
                </Button>
              }
            >
              <AlertTitle>Retenu dans votre dossier</AlertTitle>
              {euros(dommage.chiffrage.total)} pour {dommage.chiffrage.lignes}
              {' '}poste{dommage.chiffrage.lignes > 1 ? 's' : ''}, fourchette
              {' '}{euros(dommage.chiffrage.total_min)} à
              {' '}{euros(dommage.chiffrage.total_max)}.
              {resultat?.deja_chiffrees?.length > 0 && (
                <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                  Déjà présentes, non réécrites :
                  {' '}{resultat.deja_chiffrees.join(', ')}
                </Typography>
              )}
            </Alert>

            {/* Le barème est un repère, pas une limite : c'est ici que
                l'assuré arrête ce qu'il demande, devis en main s'il en a un. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}
                   alignItems={{ sm: 'center' }} sx={{ mt: 2 }}>
              <TextField
                label="Ce que vous réclamez" type="number" size="small"
                value={reclame}
                onChange={(e) => setReclame(e.target.value)}
                InputProps={{ endAdornment: (
                  <Typography variant="caption" color="text.secondary">€</Typography>
                ) }}
                sx={{ width: 200 }}
                helperText={`Le barème calcule ${euros(dommage.chiffrage.calculer)}`}
              />
              <Button variant="outlined" disabled={occupe}
                      onClick={enregistrerReclame}>
                Enregistrer ce montant
              </Button>
              {Number(dommage.chiffrage.reclamer) > 0 && (
                <Typography variant="body2" color="text.secondary">
                  Réclamé : {euros(dommage.chiffrage.reclamer)}
                </Typography>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Si votre entrepreneur vous a remis un devis, indiquez son
              montant : c'est lui qui fera foi, pas notre calcul.
            </Typography>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
}

/**
 * Quatrième étape : chiffrer chaque dommage.
 *
 * L'assuré choisit les travaux et saisit les quantités — rien n'est déduit
 * d'une photo. L'assistant, lui, éclaire l'arbitrage entre réparer et remplacer.
 */
export default function Chiffrage({ expertiseId, onSuivant, onRetour, onEtape }) {
  const [dommages, setDommages] = useState(null);
  const [erreur, setErreur] = useState('');

  const recharger = useCallback(() => {
    listerDommages(expertiseId)
      .then(setDommages)
      .catch(() => setErreur('Impossible de charger vos dommages.'));
  }, [expertiseId]);

  useEffect(recharger, [recharger]);

  return (
    <Layout
      etape="chiffrage"
      onEtape={onEtape}
      titre="Votre chiffrage"
      introduction="Pour chaque dommage, nous proposons les travaux correspondants et leur prix de référence. Ces montants vous servent de repère — vous restez libre de réclamer sur base du devis de votre entrepreneur."
      actions={
        <>
          <Button onClick={onRetour}>Retour</Button>
          <Button variant="contained" size="large" onClick={onSuivant}>
            Continuer
          </Button>
        </>
      }
    >
      {erreur && <Alert severity="error" sx={{ mb: 3 }}>{erreur}</Alert>}

      <Stack spacing={2}>
        {dommages === null && <CircularProgress size={28} />}
        {dommages?.length === 0 && (
          <Alert severity="info">
            Aucun dommage déclaré. Revenez à l'étape précédente pour en ajouter.
          </Alert>
        )}
        {dommages?.map((d) => (
          <BlocDommage key={d.id} dommage={d} onChiffre={recharger} />
        ))}
      </Stack>
    </Layout>
  );
}
